'use client';

import { Box, Typography } from '@mui/material';

import { Avatar, LiveBadge } from '@/components';
import { useT } from '@/hooks';
import { EventResponse } from '@/types';

interface TennisScoreBoardProps {
  game: EventResponse;
}

type ParsedSet = {
  home: {
    main: string;
    tb: string;
  };
  away: {
    main: string;
    tb: string;
  };
};

const parseTennisScore = (score?: string): ParsedSet[] => {
  const parsedSets: ParsedSet[] = [];

  if (score) {
    const sets = score
      .split(',')
      .map((set) => set.trim())
      .filter(Boolean);

    sets.forEach((set) => {
      const tbMatch = set.match(/\((.*?)\)/);

      let homeTb = '';
      let awayTb = '';

      if (tbMatch?.[1]) {
        const [hTb = '', aTb = ''] = tbMatch[1].split('-');

        homeTb = hTb.trim();
        awayTb = aTb.trim();
      }

      const cleanSet = set.replace(/\(.*?\)/g, '').trim();

      const [homeScore = '-', awayScore = '-'] = cleanSet.split('-').map((value) => value.trim());

      parsedSets.push({
        home: {
          main: homeScore,
          tb: homeTb,
        },
        away: {
          main: awayScore,
          tb: awayTb,
        },
      });
    });
  }

  while (parsedSets.length < 3) {
    parsedSets.push({
      home: {
        main: '-',
        tb: '',
      },
      away: {
        main: '-',
        tb: '',
      },
    });
  }

  return parsedSets;
};

const getActiveSetIndex = (period?: string) => {
  if (!period) return -1;

  const match = period.toUpperCase().match(/^S(\d+)$/);

  return match ? Number(match[1]) - 1 : -1;
};

export const TennisScoreBoard = ({ game }: TennisScoreBoardProps) => {
  const { t } = useT();
  const parsedSets = parseTennisScore(game.score);

  const activeSetIndex = getActiveSetIndex(game.period);

  const isLive = game.isLive;

  const statusText = isLive
    ? t('sports.live').toUpperCase()
    : game.period === 'FT'
      ? t('sports.final', { defaultValue: 'FINAL' }).toUpperCase()
      : t('sports.upcoming', { defaultValue: 'UPCOMING' }).toUpperCase();

  const displayStatus =
    game.period && game.period !== 'FT' ? `${statusText} · ${game.period}` : statusText;

  const StatusIcon = isLive ? (
    <LiveBadge />
  ) : (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: 'text.disabled',
      }}
    />
  );

  const renderSetBoxes = (team: 'home' | 'away') => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      {parsedSets.map((set, index) => {
        const score = set[team];
        const isActive = index === activeSetIndex;
        const isEmpty = score.main === '-';

        return (
          <Box
            key={`${team}-${index}`}
            sx={{
              minWidth: 38,
              width: 38,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              bgcolor: isActive ? 'action.hover' : 'background.subtle',
              border: '1px solid',
              borderColor: 'divider',
              opacity: isEmpty ? 0.6 : 1,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'baseline',
                lineHeight: 1,
              }}
            >
              {score.main}

              {score.tb && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.65em',
                    fontWeight: 700,
                    ml: 0.25,
                    alignSelf: 'flex-start',
                  }}
                >
                  {score.tb}
                </Box>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 700,
        mx: 'auto',
        gap: 1.5,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        p: {
          xs: 1.5,
          sm: 2.5,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: isLive ? 'error.main' : 'text.secondary',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
          }}
        >
          {StatusIcon}
          {displayStatus}
        </Typography>
      </Box>

      {/* Home Team */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            flex: 1,
            mr: 1,
          }}
        >
          <Avatar
            src={game.homeTeam?.logo}
            alt={game.homeTeam?.name || 'Home'}
            size={24}
            sx={{
              borderRadius: 0.5,
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: '0.875rem',
                sm: '1rem',
              },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {game.homeTeam?.name || 'Home Team'}
          </Typography>
        </Box>

        {renderSetBoxes('home')}
      </Box>

      {/* Away Team */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            flex: 1,
            mr: 1,
          }}
        >
          <Avatar
            src={game.awayTeam?.logo}
            alt={game.awayTeam?.name || 'Away'}
            size={24}
            sx={{
              borderRadius: 0.5,
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: '0.875rem',
                sm: '1rem',
              },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {game.awayTeam?.name || 'Away Team'}
          </Typography>
        </Box>

        {renderSetBoxes('away')}
      </Box>
    </Box>
  );
};
